/*
 OK FBI — Phase 2 pages: Plans, Leaderboard, Profile
 Client-side only. All data comes from server. Client never mutates points.
*/
import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { useAuth } from "@/pages/AuthPage";
import { Trophy, Crown, Star, Zap, ShieldCheck, Radio, User as UserIcon } from "lucide-react";
import { PackageChip } from "@/components/PackageChip";

// ============ Plans (Subscriptions) — Phase 9 cinematic ============
export function PlansPage() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [premiumIntro, setPremiumIntro] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  useEffect(() => {
    api.get("/plans").then(({data}) => {
      setPlans(data?.plans || []);
      setMatrix(data?.feature_matrix || {});
      setPremiumIntro(data?.premium_ids_section || null);
    }).catch(() => {});
    api.get("/entitlements/me").then(({data}) => setEntitlements(data)).catch(() => {});
  }, []);
  const currentPlan = entitlements?.plan || user?.membership_plan || "FREE";
  const IconMap = { FREE: ShieldCheck, VIP: Star, LEGENDARY: Crown, ROYAL: Crown, STREAMER: Radio };
  const planColumns = ["FREE", "VIP", "LEGENDARY", "ROYAL", "STREAMER"];
  const featureGroups = [
    { slug: "core_investigation",     label: "التحقيق الأساسي" },
    { slug: "basic_evidence",         label: "الأدلة الأساسية" },
    { slug: "basic_interrogation",    label: "الاستجواب الأساسي" },
    { slug: "witnesses",              label: "الشهود" },
    { slug: "basic_notes",            label: "الملاحظات" },
    { slug: "voting",                 label: "التصويت" },
    { slug: "join_squad",             label: "الانضمام إلى Squad" },
    { slug: "create_squad",           label: "إنشاء Squad" },
    { slug: "lead_squad",             label: "قيادة Squad" },
    { slug: "private_inbox",          label: "صندوق الرسائل السرية" },
    { slug: "personalized_clues",     label: "التلميحات الشخصية" },
    { slug: "private_evidence",       label: "الأدلة الخاصة" },
    { slug: "advanced_forensics",     label: "التحليل الجنائي المتقدم" },
    { slug: "investigation_history",  label: "سجل التحقيق" },
    { slug: "enhanced_stats",         label: "إحصائيات محسّنة" },
    { slug: "streamer_control_center", label: "مركز التحكم للبث" },
    { slug: "secret_random_events",   label: "الأحداث السرية" },
    { slug: "ai_narrative_director",  label: "المخرج الذكي" },
    { slug: "case_regeneration",      label: "إعادة توليد القضية" },
    { slug: "blind_investigation_mode", label: "وضع المحقق المجهول" },
    { slug: "session_analytics",      label: "تحليلات الجلسة" },
    { slug: "obs_presentation",       label: "العرض السينمائي / OBS" },
    { slug: "draft_cases",            label: "القضايا المسودة" },
    { slug: "admin_controls",          label: "صلاحيات الإدارة" },
  ];
  return (
    <Shell>
      <div dir="rtl" className="max-w-6xl mx-auto pb-24" data-testid="plans-page">
        <div className="mb-10 text-center">
          <div className="text-[11px] text-yellow-500/70 tracking-[0.35em] font-cairo mb-2">OK · FBI · CLEARANCE</div>
          <h1 className="font-cairo text-4xl md:text-5xl font-black text-gold tracking-wider">اختر مستوى تحقيقك</h1>
          <div className="text-zinc-400 text-sm font-cairo mt-3 max-w-2xl mx-auto">
            كل مستوى يفتح تجربة مختلفة داخل القضية. لا Coins، لا رصيد داخلي — اشتراكات فقط.
          </div>
          {entitlements && (
            <div className="mt-4 inline-flex items-center gap-2 text-xs font-cairo border border-yellow-800/60 bg-yellow-950/20 px-3 py-1.5 rounded">
              <span className="text-neutral-400">باقتك الحالية:</span>
              <span className="text-yellow-400 font-bold">{currentPlan}</span>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-5 gap-5">
          {plans.map(p => {
            const Icon = IconMap[p.slug] || ShieldCheck;
            const isCurrent = currentPlan === p.slug || (p.slug === "STREAMER" && currentPlan === "OWNER");
            const isStreamer = p.slug === "STREAMER";
            const isVip = p.slug === "VIP";
            return (
              <div key={p.slug} data-testid={`plan-${p.slug}`}
                className={`panel gold-border p-6 flex flex-col relative overflow-hidden ${isCurrent ? "ring-1 ring-gold" : ""} ${isStreamer ? "bg-gradient-to-b from-yellow-950/20 to-black" : ""}`}>
                {p.badge && (
                  <div className="absolute top-2 left-2 text-[10px] tracking-widest font-cairo px-2 py-0.5 rounded bg-yellow-900/40 text-yellow-300 border border-yellow-800/60">
                    {p.badge}
                  </div>
                )}
                <div className={`inline-flex items-center gap-2 border px-2 py-1 self-start text-[10px] font-cairo tracking-widest ${
                  isStreamer ? "border-yellow-700 text-yellow-300" :
                  isVip ? "border-yellow-800/60 text-yellow-200" : "border-zinc-700 text-zinc-300"
                }`}>
                  <Icon size={12} /> {p.label}
                </div>
                <div className="mt-4 mb-1 text-2xl font-cairo text-white">{p.label}</div>
                <div className="text-xs font-cairo text-yellow-300 mb-2">{p.tagline}</div>
                <div className="text-[13px] font-cairo text-neutral-400 mb-4">{p.description}</div>

                <ul className="space-y-1.5 text-[12px] text-zinc-300 font-cairo mb-6 flex-1">
                  {featureGroups.filter(fg => (matrix[fg.slug] || {})[p.slug]).slice(0, 8).map((fg) => (
                    <li key={fg.slug} className="flex items-start gap-2">
                      <span className="text-gold mt-1">✓</span> {fg.label}
                    </li>
                  ))}
                  {p.slug === "STREAMER" && (
                    <>
                      <li className="flex items-start gap-2 mt-3">
                        <span className="text-yellow-400 mt-0.5">★</span>
                        <span><b>قضية جديدة في كل مرة</b> — أعد توليد نفس القضية بقصة مختلفة تماماً.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-yellow-400 mt-0.5">★</span>
                        <span><b>حقّق معهم… دون أن تعرف الحقيقة.</b> وضع المحقق المجهول.</span>
                      </li>
                    </>
                  )}
                </ul>

                <div className="mt-auto space-y-2">
                  <div className="text-center text-[11px] font-cairo text-neutral-500 tracking-widest">
                    السعر: {p.price_note}
                  </div>
                  {isCurrent ? (
                    <div className="w-full text-center py-2 border border-gold text-gold font-cairo text-[12px] tracking-widest">
                      باقتك الحالية
                    </div>
                  ) : (
                    <button disabled data-testid={`plan-cta-${p.slug}`}
                      className="w-full py-2 border border-zinc-700 text-zinc-400 font-cairo text-[12px] tracking-widest cursor-not-allowed">
                      قريباً — التفعيل عبر الإدارة
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Feature comparison */}
        <div className="mt-12">
          <div className="text-xs font-cairo text-neutral-400 tracking-[0.35em] mb-3">مقارنة الميزات</div>
          <div className="panel gold-border overflow-hidden">
            <table className="w-full text-[12px] font-cairo">
              <thead className="bg-black/50 text-gold">
                <tr>
                  <th className="text-right p-3">الميزة</th>
                  {planColumns.map((pl) => (
                    <th key={pl} className="text-center p-3 w-24">{pl}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {featureGroups.map(fg => (
                  <tr key={fg.slug} className="border-t border-neutral-900">
                    <td className="p-3 text-zinc-300">{fg.label}</td>
                    {planColumns.map(pl => (
                      <td key={pl} className="p-3 text-center">
                        {(matrix[fg.slug] || {})[pl] ? (
                          <span className="text-emerald-400">✓</span>
                        ) : (
                          <span className="text-neutral-700">✕</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Premium Investigator IDs section */}
        {premiumIntro && (
          <div className="mt-12" data-testid="premium-ids-section">
            <div className="border border-yellow-900/50 rounded-lg p-8 bg-gradient-to-br from-yellow-950/20 to-black">
              <div className="text-[11px] text-yellow-500/70 tracking-[0.35em] font-cairo mb-1">PREMIUM · REGISTRY</div>
              <h2 className="text-2xl font-cairo font-black text-yellow-300">{premiumIntro.title}</h2>
              <div className="text-lg font-cairo text-white mt-1">{premiumIntro.headline}</div>
              <div className="text-sm font-cairo text-neutral-400 mt-1">{premiumIntro.subtitle}</div>
              <div className="mt-4 text-xs font-cairo text-neutral-500">{premiumIntro.note}</div>
              <div className="mt-6 grid grid-cols-3 md:grid-cols-6 gap-2 text-center font-cairo">
                {["111","777","999","1000","2026","888"].map(n => (
                  <div key={n} data-testid={`premium-id-tile-${n}`}
                       className="border border-yellow-900/40 py-3 rounded bg-black/60 text-yellow-200 text-lg tracking-widest">
                    #{n}
                  </div>
                ))}
              </div>
              <button disabled data-testid="premium-ids-cta"
                className="mt-6 px-6 py-2 border border-yellow-800/60 text-yellow-300 font-cairo text-[12px] tracking-widest disabled:opacity-70 cursor-not-allowed">
                قريباً — طلب رقم مميز
              </button>
            </div>
          </div>
        )}
      </div>
    </Shell>
  );
}

// ============ Leaderboard ============
export function LeaderboardPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    api.get("/leaderboard?limit=100").then(({data}) => setRows(data || []))
      .catch(() => setRows([])).finally(() => setLoading(false));
  }, []);
  return (
    <Shell>
      <div dir="rtl" className="max-w-5xl mx-auto" data-testid="leaderboard-page">
        <div className="mb-6 flex items-center gap-3">
          <Trophy className="text-gold" size={22} />
          <h1 className="font-cairo text-3xl font-black text-white">قائمة المتصدرين العالمية</h1>
        </div>
        <div className="text-zinc-500 text-[11px] font-cairo mb-4">
          النقاط تتراكم عبر جميع الجلسات مع أي ستريمر — حسابك ورتبتك عالميّة واحدة.
        </div>
        <div className="panel gold-border overflow-hidden">
          <table className="w-full text-[12px]">
            <thead className="bg-black/50 text-gold font-cairo">
              <tr>
                <th className="text-right p-3 w-12">#</th>
                <th className="text-right p-3">المحقق</th>
                <th className="text-right p-3">الرتبة</th>
                <th className="text-right p-3">العضوية</th>
                <th className="text-right p-3">النقاط</th>
                <th className="text-right p-3">قضايا</th>
                <th className="text-right p-3">إنجازات</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.user_id} className="border-t border-metal hover:bg-white/5"
                    data-testid={`lb-row-${r.agent_id}`}>
                  <td className="p-3 font-mono-ar text-gold">{r.position}</td>
                  <td className="p-3">
                    <Link to={`/profile/${r.agent_id}`} className="hover:text-gold">
                      <div className="text-white font-cairo">{r.name}</div>
                      <div className="text-zinc-500 text-[10px] font-mono-ar">#{r.agent_id} · @{r.tiktok_username || "-"}</div>
                    </Link>
                  </td>
                  <td className="p-3">
                    <span className="text-emerald-300 text-[11px] font-cairo">{r.rank?.name || "-"}</span>
                  </td>
                  <td className="p-3"><MembershipBadge plan={r.membership_plan} /></td>
                  <td className="p-3 text-gold font-mono-ar">{r.points.toLocaleString("ar-EG")}</td>
                  <td className="p-3 text-zinc-300 font-mono-ar">{r.solved_cases}</td>
                  <td className="p-3 text-zinc-400 font-mono-ar text-[11px]">
                    {r.wins}W · {r.accuracy_den ? Math.round(100*r.accuracy_num/r.accuracy_den) : 0}%
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-zinc-500 font-cairo">
                  {loading ? "جاري التحميل..." : "لا يوجد لاعبون بعد"}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Shell>
  );
}

// ============ Public Profile ============
export function PublicProfilePage() {
  const { agentId } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/users/by-agent/${agentId}`).then(({data}) => setP(data))
      .catch((e) => setErr(formatErr(e?.response?.data?.detail) || "لم يُعثر على المحقق"));
  }, [agentId]);
  if (err) return <Shell><div className="text-center py-16 text-zinc-400 font-cairo" data-testid="profile-not-found">{err}</div></Shell>;
  if (!p) return <Shell><div className="text-center py-16 text-gold font-cairo">جاري التحميل...</div></Shell>;
  const acc = p.accuracy_den ? Math.round(100*p.accuracy_num/p.accuracy_den) : 0;
  return (
    <Shell>
      <div dir="rtl" className="max-w-4xl mx-auto" data-testid="profile-page">
        <div className="panel gold-border p-6">
          <div className="flex items-center gap-5 flex-wrap">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-gold to-yellow-800 flex items-center justify-center text-black font-cairo text-3xl">
              {(p.name || "?").slice(0,1)}
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-white text-2xl font-cairo">{p.name}</div>
                <MembershipBadge plan={p.membership_plan} />
                {p.account_type === "STREAMER" && (
                  <span className="border border-purple-700 text-purple-300 px-2 py-0.5 text-[10px] font-cairo">ستريمر</span>
                )}
                {p.account_type === "OWNER" && (
                  <span className="border border-gold text-gold px-2 py-0.5 text-[10px] font-cairo">المالك</span>
                )}
              </div>
              <div className="text-zinc-500 text-[12px] font-mono-ar mt-1">
                #{p.agent_id} {p.tiktok_username && <>· @{p.tiktok_username}</>}
              </div>
              <div className="mt-2 text-emerald-300 text-[13px] font-cairo">{p.rank?.name}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <StatBox label="النقاط" value={p.points.toLocaleString("ar-EG")} testid="stat-points" />
            <StatBox label="الترتيب العالمي" value={`#${p.position}`} testid="stat-position" />
            <StatBox label="قضايا محلولة" value={p.solved_cases} testid="stat-solved" />
            <StatBox label="الدقة" value={`${acc}%`} sub={`${p.accuracy_num}/${p.accuracy_den}`} testid="stat-accuracy" />
          </div>
          <div className="mt-6 pt-4 border-t border-zinc-800/80 flex justify-between items-center">
            <Link
              to={`/investigator/${agentId}`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded bg-gold/10 border border-gold/40 text-gold text-xs font-cairo hover:bg-gold/20 transition-colors"
              data-testid="link-full-profile"
            >
              <span>الملف الشخصي الكامل والشارات</span>
              <span>←</span>
            </Link>
            <Link
              to="/leaderboard"
              className="text-xs text-zinc-500 hover:text-zinc-300 font-cairo transition-colors"
            >
              العودة للمتصدرين
            </Link>
          </div>
        </div>
      </div>
    </Shell>
  );
}

function StatBox({ label, value, sub, testid }) {
  return (
    <div className="bg-black/40 border border-zinc-800 p-4 text-center" data-testid={testid}>
      <div className="text-gold text-2xl font-cairo">{value}</div>
      <div className="text-zinc-400 text-[11px] font-cairo mt-1">{label}</div>
      {sub && <div className="text-zinc-500 text-[10px] font-mono-ar mt-0.5">{sub}</div>}
    </div>
  );
}

// ============ Reusable Membership Badge ============
// Delegates to `PackageChip` so all screens (header, tables, cards) resolve
// the same canonical `membership_plan` → label mapping, including the full
// tier ladder FREE · VIP · LEGENDARY · ROYAL · STREAMER · OWNER.  Previously
// this component had a hardcoded 4-key map that fell back to "مجاني" for
// LEGENDARY/ROYAL/VIP — the source of ANGEL's ROYAL→FREE regression.
export function MembershipBadge({ plan }) {
  const key = String(plan || "FREE").toUpperCase();
  if (key === "OWNER") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-cairo tracking-widest border border-gold text-gold"
            data-testid="badge-OWNER">
        المالك
      </span>
    );
  }
  return <PackageChip plan={key} size="sm" />;
}
